// MF-67 — Add a follow-up to a journal case: a note and/or a status change
// (open → actioned → resolved). Appends to the case timeline via the follow-up endpoint.
import { useState } from 'react'
import { TextField } from '@mui/material'
import DialogConfirm from '../../../dialog/DialogConfirm'
import FormField from './FormField'
import ChoiceCards from './ChoiceCards'
import { STATUS_COLORS } from './colorTokens'
import { inputSx } from './formStyles'
import { addFollowUp } from '../../../../services/journal/entries.service'

const STATUSES = [
  { value: 'open', label: 'Open', hint: 'New, not handled yet', color: STATUS_COLORS.open },
  { value: 'actioned', label: 'Actioned', hint: 'Being handled', color: STATUS_COLORS.actioned },
  { value: 'resolved', label: 'Resolved', hint: 'Closed', color: STATUS_COLORS.resolved }
]

const FollowUpDialog = ({ entry, onClose, onSaved }) => {
  const current = entry?.status || 'open'
  const [note, setNote] = useState('')
  const [status, setStatus] = useState(current)
  const [saving, setSaving] = useState(false)

  const statusChanged = status !== current
  const canAdd = Boolean(note.trim()) || statusChanged

  const onConfirm = async () => {
    if (!canAdd) return
    setSaving(true)
    try {
      await addFollowUp(entry._id, { note: note.trim(), status: statusChanged ? status : undefined })
      onSaved?.()
    } finally {
      setSaving(false)
    }
  }

  return (
    <DialogConfirm
      id='journal-follow-up'
      open={Boolean(entry)}
      onCancel={onClose}
      onConfirm={onConfirm}
      loading={saving}
      disabledConfirm={!canAdd}
      title='Add follow-up'
      textConfirm='Add'
      onCloseHeader
      maxWidth='sm'
    >
      <div className='flex flex-col' style={{ gap: 24, paddingTop: 8 }}>
        <FormField
          label='Case status'
          help='Open (new) → Actioned (being handled) → Resolved (closed). Setting it to Resolved records the resolution.'
          hint={`Currently ${current.charAt(0).toUpperCase() + current.slice(1)}. Pick a new status, add a note, or both.`}
        >
          <ChoiceCards options={STATUSES} value={status} onChange={setStatus} minWidth={120} />
        </FormField>
        <FormField label='Note' htmlFor='follow-up-note' hint='What happened, or what was done.'>
          <TextField
            id='follow-up-note'
            fullWidth
            size='small'
            multiline
            minRows={4}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            sx={inputSx}
          />
        </FormField>
      </div>
    </DialogConfirm>
  )
}

export default FollowUpDialog
